import { useEffect, useMemo , useCallback, useState, useRef } from 'react';
import Bluebird from 'bluebird';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';
import DynamicForm from '@/components/Questionnaire/form/DynamicForm';
import { Questionnaire } from '@/typings/Questionnaire';
import ProgressIndicator from '@/components/ProgressIndicator';
import Spacer from '@/components/Spacer';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import useToast from '@/hooks/useToast';
import trackEvents from '@/helpers/trackEvents';
import { useQuestionnaireTemplate, useQuestionnaireFieldTemplates, usePrioritizeReplication } from '@/providers/RxDB/hooks';
import NotEditableModal from './NotEditableModal';

type Props = {
  id: string;
  onComplete: () => void;
  isCompleted: boolean;
  setFormVersion: (version: number | null) => void;
  userWorkflowId: string | null;
  shouldForceComplete?: boolean;
  onReload: () => void;
};

type QuestionnaireResponses = Awaited<ReturnType<typeof tsClient.questionnaires.getQuestionnaireResponses>>['items'];

const QuestionnaireDetails: React.FC<Props> = ({
  setFormVersion,
  id,
  onComplete,
  isCompleted,
  onReload,
  userWorkflowId,
  shouldForceComplete,
}) => {
  trackEvents.track('Render QuestionnaireDetails');
  const [questionnaireResponses, setQuestionnaireResponses] = useState<QuestionnaireResponses | null>(null);
  const [isFetchingResponses, setIsFetchingResponses] = useState(true);
  const addToast = useToast();
  const [isEnabled, setIsEnabled] = useState(!isCompleted);
  const isEnabledRef = useRef(isEnabled);
  isEnabledRef.current = isEnabled;
  const [errorReason, setErrorReason] = useState<string | null>(null);

  const [sectionFields, setSectionFields] = useState<Record<string, QuestionnaireApiModels.FieldTemplateItem[]>>({});

  const shouldEnableRxDBCache = useFeatureFlag('rx-db-questionnaire-template-while-answering-cache');
  const enableRxDBFull = useFeatureFlag('rx-db-questionnaire-template-while-answering-full');
  const enableRxDBCache = !enableRxDBFull && shouldEnableRxDBCache;
  const enableRxDB = enableRxDBCache || enableRxDBFull;

  const {
    data: questionnaireTemplateData,
    getData: getQuestionnaireTemplate,
  } = useNoodleApi(tsClient.questionnaires.getQuestionnaireTemplateOfQuestionnaire);

  const {
    getData: getQuestionnaireResponses,
  } = useNoodleApi(tsClient.questionnaires.getQuestionnaireResponses);

  const {
    getData: changeQuestionnaireStatus,
  } = useNoodleApi(tsClient.questionnaires.changeQuestionnaireStatus);

  const { getData: getFieldTemplates } = useNoodleApi(tsClient.questionnaires.getFieldTemplatesForSectionTemplate);

  const { getData: updateQuestionnaireResponses } = useNoodleApi(tsClient.questionnaires.updateQuestionnaireResponses, {
    toastOnError: true,
  });

  const {
    data: questionnaire,
    getData: getQuestionnaireById,
  } = useNoodleApi(tsClient.questionnaires.getQuestionnaireById);

  usePrioritizeReplication({
    questionnaireFieldTemplate: questionnaire?.questionnaireTemplateId,
    questionnaireTemplate: [],
  });

  const rxdbQuestionnaireTemplate = useQuestionnaireTemplate(questionnaire?.questionnaireTemplateId, { enabled: enableRxDB });
  const rxdbFieldTemplates = useQuestionnaireFieldTemplates(questionnaire?.questionnaireTemplateId, { enabled: enableRxDB });

  const questionnaireTemplate = enableRxDBFull ? rxdbQuestionnaireTemplate : questionnaireTemplateData;

  useEffect(() => {
    if (!enableRxDBFull) {
      getQuestionnaireTemplate({ questionnaireId: id }, { cache: enableRxDBCache });
    }
  }, [getQuestionnaireTemplate, id, enableRxDBFull, enableRxDBCache]);

  useEffect(() => {
    const newIsEnabled = !isCompleted;
    setIsEnabled(newIsEnabled);
    isEnabledRef.current = newIsEnabled;
  }, [isCompleted]);

  useEffect(() => {
    getQuestionnaireById({ questionnaireId: id });
  }, [getQuestionnaireById, id]);

  const getFieldTemplatesForSectionTemplate = useCallback(async (
    section: Pick<QuestionnaireApiModels.SectionTemplate, 'id' | 'slug' | 'questionnaireTemplateId'>,
  ): Promise<void> => {
    if (!enableRxDBFull && questionnaireTemplate?.creatorId && section.id) {
      const { data: fields } = await getFieldTemplates({
        creatorId: questionnaireTemplate.creatorId,
        questionnaireTemplateId: section.questionnaireTemplateId,
        sectionTemplateId: section.id,
      }, { cache: enableRxDBCache });
      if (fields) {
        setSectionFields(prev => ({
          ...prev,
          [section.id]: fields.map((field) => ({
            ...field,
            assignedTo: field.fieldAssigneeTemplates?.map((assignee) => assignee.questionnaireAssigneeTemplateId) ?? [],
            description: field.descriptionText ?? '',
            disabled: isCompleted,
            id: field.id,
            validation: field.validation || undefined,
          })),
        }));
      }
    }
  }, [questionnaireTemplate?.creatorId, getFieldTemplates, isCompleted, enableRxDBFull, enableRxDBCache]);

  // @ts-expect-error @todo: fix validation typing issue later
  const formData: Questionnaire.Questionnaire | undefined = useMemo(() =>
    questionnaireTemplate
      ? ({
        ...questionnaireTemplate,
        assigneeTemplates: questionnaireTemplate.questionnaireAssigneeTemplates,
        creatorId: questionnaireTemplate.creatorId,
        description: questionnaireTemplate.descriptionText ?? '',
        id,
        questionnaireTemplateCreatorId: questionnaireTemplate.creatorId,
        questionnaireTemplateId: questionnaireTemplate.id,
        sections: (questionnaireTemplate.sectionTemplates ?? [])
          .map((section) => ({
            ...section,
            description: section.descriptionText ?? '',
            // @todo: support html text later
            fields: sectionFields[section.id] ?? [],
            id: section.id,
            isLoading: !sectionFields[section.id],
          }))
          .sort((a, b) => {
            const rankA = a.rank ?? Number.MAX_SAFE_INTEGER;
            const rankB = b.rank ?? Number.MAX_SAFE_INTEGER;
            return rankA - rankB;
          }),
      })
      : undefined,
  [sectionFields, questionnaireTemplate, id],
  );

  const addSectionFieldsFromRxDB = useCallback(() => {
    const newSectionFields = rxdbFieldTemplates?.reduce<Record<string, QuestionnaireApiModels.FieldTemplateItem[]>>((acc, field) => ({
      ...acc,
      [field.sectionTemplateId]: [
        ...(acc[field.sectionTemplateId] ?? []),
        {
          ...field,
          assignedTo: field.fieldAssigneeTemplates?.map((assignee) => assignee.questionnaireAssigneeTemplateId) ?? [],
          description: field.descriptionText ?? '',
          disabled: isCompleted,
          id: field.id!,
          validation: field.validation || undefined,
        },
      ],
    }), {}) || {};
    setSectionFields(newSectionFields);
  }, [rxdbFieldTemplates, isCompleted]);

  useEffect(() => {
    if (enableRxDBFull) {
      addSectionFieldsFromRxDB();
    }
  }, [addSectionFieldsFromRxDB, enableRxDBFull]);

  useEffect(() => {
    if (questionnaireTemplate?.sectionTemplates) {
      Bluebird.mapSeries(questionnaireTemplate.sectionTemplates.sort((a, b) => a.rank - b.rank), getFieldTemplatesForSectionTemplate);
    }
  }, [questionnaireTemplate, getFieldTemplatesForSectionTemplate]);

  useEffect(() => {
    setFormVersion(questionnaireTemplate?.version ?? null);
  }, [questionnaireTemplate?.version]);

  useEffect(() => {
    const fetchResponses = async (): Promise<void> => {
      setIsFetchingResponses(true);
      setQuestionnaireResponses(null);
      const getResponsesResponse = await getQuestionnaireResponses({
        page: 1,
        perPage: 100_000,
        questionnaireId: id,
      });

      if (getResponsesResponse.data) {
        setQuestionnaireResponses(getResponsesResponse.data.items);
      } else {
        setQuestionnaireResponses(null);
      }
      setIsFetchingResponses(false);
    };
    fetchResponses();
  }, [getQuestionnaireResponses, id]);

  const handleUpdateQuestionnaire = useCallback(async (
    data: QuestionnaireApiModels.UpdateResponseData,
    isNowCompleted?: boolean,
  ): Promise<boolean> => {
    if (!isEnabledRef.current) {
      return false;
    }

    const res = await updateQuestionnaireResponses({
      questionnaireId: id,
    }, data);

    if (res.error) {
      if (
        res.error
        && res.error.errors
        && Array.isArray(res.error.errors)
        && res.error.errors.length > 0
        && res.error.errors[0] === 'Can only update questionnaire responses for a questionnaire that is in-progress'
      ) {
        isEnabledRef.current = false;
        setIsEnabled(false);
        if (
          res.error
          && 'data' in res.error
          && res.error.data
          && typeof res.error.data === 'object'
          && 'reason' in res.error.data
          && typeof res.error.data.reason === 'string'
        ) {
          setErrorReason(res.error.data.reason);
        } else {
          setErrorReason('unknown');
        }
      }
      return false;
    }

    if (isNowCompleted) {
      await changeQuestionnaireStatus({
        questionnaireId: id,
      },{
        status: 'submitted',
      });

      onComplete();
    }

    return true;
  }, [id, onComplete, changeQuestionnaireStatus, updateQuestionnaireResponses, addToast]);

  const fieldsByKey: Record<string, Questionnaire.Field> = useMemo(() => {
    if (!formData) return {};

    return formData.sections.reduce((acc: Record<string, Questionnaire.Field>, section) => {
      section.fields.forEach((field) => {
        if (!field.isVirtual && !acc[field.key]) {
          acc[field.key] = field;
        }
      });
      return acc;
    }, {});
  }, [formData]);

  if (!questionnaireTemplate || isFetchingResponses || !questionnaireResponses) {
    return <><Spacer /><ProgressIndicator isCentered /></>;
  }

  if (!formData) {
    return null;
  }
  return (
    <>
      {errorReason && (
        <NotEditableModal
          reason={errorReason}
          onClose={() => setErrorReason(null)}
          onReload={onReload}
        />
      )}
      <DynamicForm
        getFieldTemplatesForSectionTemplate={getFieldTemplatesForSectionTemplate}
        fieldsByKey={fieldsByKey}
        formData={formData}
        questionnaireResponses={questionnaireResponses}
        onSubmit={handleUpdateQuestionnaire}
        isCompleted={isCompleted}
        isEnabled={isEnabled}
        userWorkflowId={userWorkflowId}
        shouldForceComplete={shouldForceComplete}
      />
    </>
  );
};

export default QuestionnaireDetails;
